import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  FolderTree,
  List,
  ChevronRight,
  ChevronDown,
  Download,
  Lock,
  FileText,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

import PageHeader from '../../components/layout/PageHeader';
import archiveService from '../../services/archive.service';
import documentService from '../../services/document.service';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuthStore } from '../../store/authStore';
import { formatDocumentDisplayName } from '../../utils/documentName';
import {
  canDownloadDocument,
  canPreviewDocument,
} from '../../utils/documentAccessMode';
import {
  buildStoragePathTree,
  comparePathLabels,
  type StoragePathNode,
} from '../../utils/storagePathTree';
import type {
  ArchivedDocument,
  ArchiveViewMode,
} from '../../types/archive.types';

/** Archived documents carrying their storage path (Section / … / Year / Date). */
interface ArchiveTreeDoc {
  document_id: string;
  original_file_name?: string | null;
  file_extension?: string | null;
  mime_type?: string | null;
  document_type?: string | null;
  classification?: string | null;
  archived_at?: string | null;
  cms_number?: string | null;
  can_view?: boolean;
  path_segments: string[];
}
type ArchivePathNode = StoragePathNode<ArchiveTreeDoc>;

const LIST_PAGE_SIZE = 10;
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = ['All', ...Array.from({ length: 5 }, (_, i) => String(CURRENT_YEAR - i))];

function mapListRow(row: any): ArchivedDocument {
  const c = row.case ?? {};
  return {
    document_id: row.document_id,
    original_file_name: row.original_file_name ?? row.file_name ?? '—',
    file_name: formatDocumentDisplayName({
      cmsNumber: c.cms_number ?? row.cms_number,
      originalFileName: row.original_file_name ?? row.file_name ?? '—',
      documentType: row.document_type ?? '—',
    }),
    file_extension: row.file_extension ?? null,
    mime_type: row.mime_type ?? null,
    folder_type: row.folder_type ?? '—',
    document_type: row.document_type ?? '—',
    classification: row.classification ?? 'Public',
    archived_at: row.updated_at ?? row.archived_at ?? row.created_at ?? '',
    archived_by_name: row.archived_by_name ?? '—',
    case_ref_number: c.rrms_ref_number ?? '—',
    cms_number: c.cms_number ?? '—',
    case_type: c.case_type ?? '—',
    ps_name: c.ps_name ?? '—',
    year: c.year ?? CURRENT_YEAR,
    approval_status: row.approval_status ?? '—',
    // Upload-form redesign display fields
    file_number: c.fir_number ?? row.fir_number ?? '—',
    hierarchy_path: c.hierarchy_path ?? row.hierarchy_path ?? null,
    category: c.category ?? row.category ?? null,
    subject: c.subject ?? row.subject ?? null,
  };
}

const ArchivePage: React.FC = () => {
  const { can } = useAuthStore();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<ArchiveViewMode>('list');
  const [year, setYear] = useState<string>(String(CURRENT_YEAR));
  const [caseType, setCaseType] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [expandedTree, setExpandedTree] = useState<Set<string>>(new Set());

  const debouncedCaseType = useDebounce(caseType, 400);
  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    document.title = 'RRMS | Archive';
  }, []);

  useEffect(() => {
    setPage(1);
  }, [year, debouncedCaseType, debouncedSearch]);

  const listParams = {
    page,
    limit: LIST_PAGE_SIZE,
    ...(year !== 'All' && { year: parseInt(year, 10) }),
    ...(debouncedCaseType && { case_type: debouncedCaseType }),
    ...(debouncedSearch && { search: debouncedSearch }),
  };

  const listQuery = useQuery({
    queryKey: ['archive-list', page, year, debouncedCaseType, debouncedSearch],
    queryFn: () => archiveService.getList(listParams),
    enabled: viewMode === 'list',
    select: (r) => {
      const d = r.data?.data ?? r.data;
      const items = (d?.items ?? []).map(mapListRow);
      const pagination = d?.pagination ?? {
        page: 1,
        limit: LIST_PAGE_SIZE,
        total: 0,
        totalPages: 0,
      };
      return { items, pagination };
    },
  });

  const treeQuery = useQuery({
    queryKey: ['archive-tree', year === 'All' ? undefined : parseInt(year, 10)],
    queryFn: () =>
      archiveService.getTree(year === 'All' ? undefined : parseInt(year, 10)),
    enabled: viewMode === 'tree',
    select: (r) => {
      const d = r.data?.data ?? r.data;
      return (d?.documents ?? []) as ArchiveTreeDoc[];
    },
  });

  const canDownloadConfidential = can('records.approve', [
    'RRMS_CONTENT_MANAGER',
    'RRMS_SUPER_ADMIN',
  ]);

  const handleDownload = (doc: ArchivedDocument) => {
    const canDownloadByType = canDownloadDocument({
      fileName: doc.original_file_name ?? doc.file_name,
      fileExtension: doc.file_extension,
      mimeType: doc.mime_type,
    });
    if (!canDownloadByType) {
      toast.error('Download is not available for this file type');
      return;
    }
    if (doc.classification === 'Confidential' && !canDownloadConfidential) {
      toast.error('Access required to download confidential documents');
      return;
    }
    documentService
      .downloadDocument(
        doc.document_id,
        formatDocumentDisplayName({
          cmsNumber: doc.cms_number,
          originalFileName: doc.file_name,
          documentType: doc.document_type,
        })
      )
      .catch(() => toast.error('Download failed'));
  };

  const handlePreview = (doc: ArchivedDocument) => {
    const canPreviewByType = canPreviewDocument({
      fileName: doc.original_file_name ?? doc.file_name,
      fileExtension: doc.file_extension,
      mimeType: doc.mime_type,
    });
    if (!canPreviewByType) {
      toast.error('Preview is not available for this file type');
      return;
    }
    if (doc.classification === 'Confidential' && !canDownloadConfidential) {
      toast.error('Access required to view confidential documents');
      return;
    }
    const url = documentService.getFileUrl(doc.document_id);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const toggleTree = (id: string) => {
    setExpandedTree((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Recursive renderer over the storage layout — Section / Sub-Section /
  // File Category / Year / File Start Date — with the archived files
  // listed inside their date folder.
  const renderArchiveLevel = (
    node: ArchivePathNode,
    segments: string[],
    depth: number
  ): React.ReactNode[] =>
    Object.keys(node.children)
      .sort(comparePathLabels)
      .map((label) => {
        const child = node.children[label];
        const childPath = [...segments, label];
        const id = `path:${childPath.join('/')}`;
        const expanded = expandedTree.has(id);
        const fileCount = child.items.length;
        return (
          <div
            key={id}
            style={
              depth > 0
                ? { paddingLeft: 'var(--spacing-6)', marginTop: 'var(--spacing-1)' }
                : { marginBottom: 'var(--spacing-2)' }
            }
          >
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleTree(id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') toggleTree(id);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                padding: 'var(--spacing-2) var(--spacing-3)',
                cursor: 'pointer',
                borderRadius: 'var(--radius-sm)',
                background: expanded
                  ? 'var(--color-primary-50)'
                  : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!expanded)
                  e.currentTarget.style.background = 'var(--color-gray-50)';
              }}
              onMouseLeave={(e) => {
                if (!expanded)
                  e.currentTarget.style.background = 'transparent';
              }}
            >
              {expanded ? (
                <ChevronDown size={depth === 0 ? 16 : 14} />
              ) : (
                <ChevronRight size={depth === 0 ? 16 : 14} />
              )}
              <span
                style={{
                  fontWeight: depth === 0 ? 500 : 400,
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {label}
              </span>
              {fileCount > 0 && (
                <span
                  style={{
                    fontSize: '10px',
                    background: 'var(--color-gray-200)',
                    color: 'var(--color-gray-600)',
                    padding: '1px 6px',
                    borderRadius: 'var(--radius-full)',
                  }}
                >
                  {fileCount} file{fileCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {expanded && renderArchiveLevel(child, childPath, depth + 1)}
            {expanded &&
              child.items.map((doc) => {
                const isConfidential = doc.classification === 'Confidential';
                const canDownloadByType = canDownloadDocument({
                  fileName: doc.original_file_name,
                  fileExtension: doc.file_extension,
                  mimeType: doc.mime_type,
                });
                const canDl = canDownloadConfidential || !isConfidential;
                const displayName = formatDocumentDisplayName({
                  cmsNumber: doc.cms_number ?? undefined,
                  originalFileName: doc.original_file_name ?? '—',
                  documentType: doc.document_type ?? undefined,
                });
                return (
                  <div
                    key={doc.document_id}
                    style={{
                      marginLeft: 'var(--spacing-6)',
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    <FileText
                      size={12}
                      style={{ color: 'var(--color-gray-400)' }}
                    />
                    <span
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {displayName}
                    </span>
                    {doc.archived_at && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-gray-400)',
                        }}
                      >
                        {format(new Date(doc.archived_at), 'dd MMM yyyy')}
                      </span>
                    )}
                    {isConfidential && !canDl ? (
                      <button
                        type="button"
                        onClick={() =>
                          toast.error(
                            'Access required to download confidential documents'
                          )
                        }
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--color-gray-400)',
                          padding: 2,
                        }}
                      >
                        <Lock size={14} />
                      </button>
                    ) : canDownloadByType ? (
                      <button
                        type="button"
                        onClick={() => {
                          documentService
                            .downloadDocument(doc.document_id, displayName)
                            .catch(() => toast.error('Download failed'));
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--color-primary)',
                          padding: 2,
                        }}
                      >
                        <Download size={14} />
                      </button>
                    ) : null}
                  </div>
                );
              })}
          </div>
        );
      });

  const listItems = listQuery.data?.items ?? [];
  const listPagination = listQuery.data?.pagination;
  const treeDocs = React.useMemo(() => treeQuery.data ?? [], [treeQuery.data]);
  // Nested view of the storage layout: Section / Sub-Section / File Category /
  // Year / File Start Date, with the archived files on the date node.
  const archiveTree = React.useMemo(
    () => buildStoragePathTree(treeDocs),
    [treeDocs]
  );

  const canManageArchive = can('records.manage_content', [
    'RRMS_CONTENT_MANAGER',
    'RRMS_SUPER_ADMIN',
  ]);

  const unarchiveMutation = useMutation({
    mutationFn: (documentId: string) => archiveService.unarchive(documentId),
    onSuccess: () => {
      toast.success('Document restored to active list.');
      queryClient.invalidateQueries({ queryKey: ['archive-list'] });
      queryClient.invalidateQueries({ queryKey: ['archive-tree'] });
      queryClient.invalidateQueries({ queryKey: ['content-tree'] });
      queryClient.invalidateQueries({ queryKey: ['case-documents'] });
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message || 'Failed to unarchive document.'
      );
    },
  });

  return (
    <div className="">
      <PageHeader
        title="Archive"
        subtitle="Archived documents from all cases"
      />

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'var(--spacing-3)',
          marginBottom: 'var(--spacing-5)',
        }}
      >
        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          style={{
            padding: 'var(--spacing-2) var(--spacing-3)',
            border: '1px solid var(--color-gray-200)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            background: 'white',
          }}
        >
          {YEAR_OPTIONS.map((y) => (
            <option key={y} value={y}>
              {y === 'All' ? 'All Years' : y}
            </option>
          ))}
        </select>

        {/*
          Hidden as per UX request: keep Case Type and Search File Name controls
          in code for easy re-enable later.
        <input
          type="text"
          placeholder="Case type"
          value={caseType}
          onChange={(e) => setCaseType(e.target.value)}
          disabled={viewMode === 'tree'}
          style={{
            padding: 'var(--spacing-2) var(--spacing-3)',
            border: '1px solid var(--color-gray-200)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            width: 140,
            opacity: viewMode === 'tree' ? 0.6 : 1,
          }}
        />

        <input
          type="text"
          placeholder="Search file name / CMS..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={viewMode === 'tree'}
          style={{
            padding: 'var(--spacing-2) var(--spacing-3)',
            border: '1px solid var(--color-gray-200)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            width: 200,
            opacity: viewMode === 'tree' ? 0.6 : 1,
          }}
        />
        */}

        <div
          style={{
            display: 'flex',
            border: '1px solid var(--color-gray-200)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          <button
            type="button"
            onClick={() => setViewMode('tree')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-4)',
              border: 'none',
              background: viewMode === 'tree' ? 'var(--color-primary-50)' : 'white',
              color: viewMode === 'tree' ? 'var(--color-primary)' : 'var(--color-gray-600)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            <FolderTree size={16} /> Tree View
          </button>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-4)',
              border: 'none',
              borderLeft: '1px solid var(--color-gray-200)',
              background: viewMode === 'list' ? 'var(--color-primary-50)' : 'white',
              color: viewMode === 'list' ? 'var(--color-primary)' : 'var(--color-gray-600)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            <List size={16} /> List View
          </button>
        </div>
      </div>

      {viewMode === 'list' && (
        <div className="card">
          {listQuery.isLoading && (
            <div style={{ padding: 'var(--spacing-4)' }}>
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: 48,
                    background: 'var(--color-gray-100)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--spacing-2)',
                  }}
                />
              ))}
            </div>
          )}

          {listQuery.isError && (
            <div
              style={{
                padding: 'var(--spacing-10)',
                textAlign: 'center',
                color: 'var(--color-error)',
              }}
            >
              Failed to load archive. Please try again.
            </div>
          )}

          {!listQuery.isLoading && !listQuery.isError && listItems.length === 0 && (
            <div
              style={{
                padding: 'var(--spacing-16)',
                textAlign: 'center',
                color: 'var(--color-gray-500)',
              }}
            >
              <Archive
                size={48}
                style={{ margin: '0 auto var(--spacing-4)', display: 'block' }}
              />
              <p style={{ fontWeight: 'var(--font-weight-medium)' }}>
                No archived documents found
              </p>
            </div>
          )}

          {!listQuery.isLoading &&
            !listQuery.isError &&
            listItems.length > 0 && (
              <>
                <div className="table-responsive">
                  <table
                    style={{
                      width: '100%',
                      minWidth: 900,
                      borderCollapse: 'collapse',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: 'var(--color-gray-50)',
                          borderBottom: '1px solid var(--color-gray-200)',
                        }}
                      >
                        <th
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            textAlign: 'left',
                            fontWeight: 600,
                          }}
                        >
                          File Name
                        </th>
                        <th
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            textAlign: 'left',
                            fontWeight: 600,
                          }}
                        >
                          File No / Ref Number
                        </th>
                        <th
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            textAlign: 'left',
                            fontWeight: 600,
                          }}
                        >
                          Section
                        </th>
                        <th
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            textAlign: 'left',
                            fontWeight: 600,
                          }}
                        >
                          Category / Subject
                        </th>
                        <th
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            textAlign: 'left',
                            fontWeight: 600,
                          }}
                        >
                          Classification
                        </th>
                        <th
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            textAlign: 'left',
                            fontWeight: 600,
                          }}
                        >
                          Archived At
                        </th>
                        <th
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            textAlign: 'right',
                            fontWeight: 600,
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {listItems.map((doc: ArchivedDocument, idx: number) => (
                        <tr
                          key={doc.document_id}
                          style={{
                            borderBottom: '1px solid var(--color-gray-100)',
                            background:
                              idx % 2 === 0
                                ? 'white'
                                : 'var(--color-gray-50)',
                          }}
                        >
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                            }}
                          >
                            {canPreviewDocument({
                              fileName: doc.original_file_name ?? doc.file_name,
                              fileExtension: doc.file_extension,
                              mimeType: doc.mime_type,
                            }) ? (
                              <button
                                type="button"
                                onClick={() => handlePreview(doc)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  cursor: 'pointer',
                                  color: 'var(--color-primary)',
                                  fontWeight: 500,
                                  textAlign: 'left',
                                  maxWidth: 220,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block',
                                }}
                              >
                                {doc.file_name}
                              </button>
                            ) : (
                              <span
                                style={{
                                  color: 'var(--color-primary)',
                                  fontWeight: 500,
                                  textAlign: 'left',
                                  maxWidth: 220,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block',
                                }}
                              >
                                {doc.file_name}
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              fontFamily: 'var(--font-family-mono)',
                              fontSize: 'var(--font-size-xs)',
                            }}
                          >
                            <div>{(doc as any).file_number !== '—' ? (doc as any).file_number : doc.cms_number}</div>
                            <div
                              style={{
                                color: 'var(--color-gray-500)',
                                fontSize: 'var(--font-size-xs)',
                              }}
                            >
                              {doc.case_ref_number}
                            </div>
                          </td>
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              color: 'var(--color-gray-600)',
                            }}
                          >
                            {(doc as any).hierarchy_path || doc.ps_name}
                          </td>
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              color: 'var(--color-gray-600)',
                            }}
                          >
                            <div>{(doc as any).category || '—'}</div>
                            {(doc as any).subject && (
                              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>
                                {(doc as any).subject}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: 'var(--spacing-3) var(--spacing-4)' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: 'var(--radius-full)',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 500,
                                background:
                                  doc.classification === 'Confidential'
                                    ? 'var(--color-error-light)'
                                    : 'var(--color-success-light)',
                                color:
                                  doc.classification === 'Confidential'
                                    ? 'var(--color-error)'
                                    : 'var(--color-success)',
                              }}
                            >
                              {doc.classification}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--color-gray-600)',
                            }}
                          >
                            {doc.archived_at
                              ? format(
                                  new Date(doc.archived_at),
                                  'dd MMM yyyy'
                                )
                              : '—'}
                            {doc.archived_by_name && doc.archived_by_name !== '—' && (
                              <div
                                style={{
                                  color: 'var(--color-gray-400)',
                                  marginTop: 2,
                                }}
                              >
                                {doc.archived_by_name}
                              </div>
                            )}
                          </td>
                          <td
                            style={{
                              padding: 'var(--spacing-3) var(--spacing-4)',
                              textAlign: 'right',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 'var(--spacing-2)',
                              }}
                            >
                            {doc.classification === 'Confidential' &&
                            !canDownloadConfidential ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    toast.error(
                                      'Access required to download confidential documents'
                                    )
                                  }
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--color-gray-400)',
                                    padding: 4,
                                  }}
                                  title="Access required"
                                >
                                  <Lock size={18} />
                                </button>
                            ) : canDownloadDocument({
                                fileName: doc.original_file_name ?? doc.file_name,
                                fileExtension: doc.file_extension,
                                mimeType: doc.mime_type,
                              }) ? (
                                <button
                                  type="button"
                                  onClick={() => handleDownload(doc)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--color-primary)',
                                    padding: 4,
                                  }}
                                  title="Download"
                                >
                                  <Download size={18} />
                                </button>
                            ) : null}
                              {canManageArchive && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        'Restore this document to the active list?'
                                      )
                                    ) {
                                      unarchiveMutation.mutate(doc.document_id);
                                    }
                                  }}
                                  disabled={unarchiveMutation.isPending}
                                  style={{
                                    padding: '4px 10px',
                                    borderRadius: 'var(--radius-md)',
                                    border: '1px solid var(--color-gray-300)',
                                    background: 'white',
                                    color: 'var(--color-gray-700)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {unarchiveMutation.isPending
                                    ? 'Restoring...'
                                    : 'Unarchive'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {listPagination && listPagination.totalPages > 1 && (
                  <div
                    style={{
                      padding: 'var(--spacing-4)',
                      borderTop: '1px solid var(--color-gray-200)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 'var(--spacing-3)',
                    }}
                  >
                    <p
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-gray-500)',
                      }}
                    >
                      Page {listPagination.page} of {listPagination.totalPages}{' '}
                      ({listPagination.total} total)
                    </p>
                    <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={!listPagination.hasPrevPage}
                        style={{
                          padding: 'var(--spacing-1) var(--spacing-3)',
                          border: '1px solid var(--color-gray-200)',
                          borderRadius: 'var(--radius-md)',
                          background: 'white',
                          cursor: listPagination.hasPrevPage
                            ? 'pointer'
                            : 'not-allowed',
                          opacity: listPagination.hasPrevPage ? 1 : 0.5,
                          fontSize: 'var(--font-size-sm)',
                        }}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setPage((p) =>
                            Math.min(listPagination.totalPages, p + 1)
                          )
                        }
                        disabled={!listPagination.hasNextPage}
                        style={{
                          padding: 'var(--spacing-1) var(--spacing-3)',
                          border: '1px solid var(--color-gray-200)',
                          borderRadius: 'var(--radius-md)',
                          background: 'white',
                          cursor: listPagination.hasNextPage
                            ? 'pointer'
                            : 'not-allowed',
                          opacity: listPagination.hasNextPage ? 1 : 0.5,
                          fontSize: 'var(--font-size-sm)',
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
        </div>
      )}

      {viewMode === 'tree' && (
        <div className="card" style={{ padding: 'var(--spacing-4)' }}>
          {treeQuery.isLoading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: 'var(--spacing-10)',
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  border: '2px solid var(--color-gray-200)',
                  borderTopColor: 'var(--color-primary)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            </div>
          )}

          {treeQuery.isError && (
            <div
              style={{
                padding: 'var(--spacing-10)',
                textAlign: 'center',
                color: 'var(--color-error)',
              }}
            >
              Failed to load archive tree.
            </div>
          )}

          {!treeQuery.isLoading &&
            !treeQuery.isError &&
            treeDocs.length === 0 && (
              <div
                style={{
                  padding: 'var(--spacing-16)',
                  textAlign: 'center',
                  color: 'var(--color-gray-500)',
                }}
              >
                <Archive
                  size={48}
                  style={{ margin: '0 auto var(--spacing-4)', display: 'block' }}
                />
                <p>No archived documents for the selected year.</p>
              </div>
            )}

          {!treeQuery.isLoading &&
            !treeQuery.isError &&
            treeDocs.length > 0 && (
              <div style={{ paddingLeft: 0 }}>
                {renderArchiveLevel(archiveTree, [], 0)}
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default ArchivePage;
